import React, { type FunctionComponent } from 'react'
import { SimpleSelect, Label } from '@retailer-platform/shared-components'
import { type FormContextValues, type Control, Controller } from 'react-hook-form'
import styled from '@emotion/styled'
import { useDomainMessages } from 'domains/collections/utils/domain/intl'
import { CollectionRuleType } from 'domains/collections/components/collections-form/components/rules/utils/CollectionsRules.types'
import { CollectionsRuleSearchTerm } from 'domains/collections/components/collections-form/components/rules/CollectionsRuleSearchTerm'
import { CollectionsRuleProductCodes } from 'domains/collections/components/collections-form/components/rules/CollectionsRuleProductCodes'
import { type CollectionsFormData } from 'domains/collections/components/collections-form/utils/collectionsForm.types'
import { CollectionFormInputWrapper } from 'domains/collections/components/collections-form/utils/collectionForm.styles'
import { CollectionsRuleDelete } from 'domains/collections/components/collections-form/components/rules/CollectionsRuleDelete'
import { CollectionsFormInput } from 'domains/collections/components/collections-form/components/form-input/CollectionsFormInput'
import { CollectionsFormField } from 'domains/collections/components/collections-form/components/form-field/CollectionsFormField'

interface CollectionsRuleProps {
  register: FormContextValues<CollectionsFormData>['register']
  control: Control<CollectionsFormData>
  deleteRule: () => void
  formPrefix: string
  watch: FormContextValues<CollectionsFormData>['watch']
  retailerId: string
  errors: FormContextValues<CollectionsFormData>['errors']
}

const collectionRuleCollectionMap = {
  [CollectionRuleType.searchTerm]: CollectionsRuleSearchTerm,
  [CollectionRuleType.productCodes]: CollectionsRuleProductCodes,
}

const RuleControls = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
})

export const CollectionsRule: FunctionComponent<CollectionsRuleProps> = ({
  deleteRule,
  formPrefix,
  register,
  control,
  watch,
  retailerId,
  errors,
}) => {
  const messages = useDomainMessages({
    label: 'collectionsDomain.collectionsForm.section.rules.options.label',
    searchTerm: 'collectionsDomain.collectionsForm.section.rules.options.searchTerm',
    productCodes: 'collectionsDomain.collectionsForm.section.rules.options.productCodes',
  })

  const ruleOptions: {
    label: string
    value: CollectionRuleType
  }[] = [
    {
      label: messages.searchTerm,
      value: CollectionRuleType.searchTerm,
    },
    {
      label: messages.productCodes,
      value: CollectionRuleType.productCodes,
    },
  ]

  const selectedRuleType = watch(`${formPrefix}.type`) as CollectionRuleType

  const RuleComponent = collectionRuleCollectionMap[selectedRuleType]

  return (
    <React.Fragment>
      <RuleControls>
        <div>
          <CollectionFormInputWrapper>
            <CollectionsFormField>
              <CollectionsFormInput
                labelId="collectionsDomain.collectionsForm.section.rules.name.label"
                errorMessage="collectionsDomain.collectionsForm.section.rules.name.error.required"
                identifier={`${formPrefix}.name`}
                ref={register({ required: true })}
                errors={errors}
              />
            </CollectionsFormField>
          </CollectionFormInputWrapper>
          <CollectionsFormField>
            <Label>{messages.label}</Label>
            {/*
              .subjectId field will never be set by a user. It is used to keep track of the existing subject
              when the form is being used for editting. It will be set on initial load using the api data.
              Rules with no subjectId will create a new subject, rules with a subjectId will update the existing subject
            */}
            <input type="hidden" name={`${formPrefix}.subjectId`} ref={register()} />
            <Controller
              as={SimpleSelect}
              name={`${formPrefix}.type`}
              control={control}
              options={ruleOptions}
            />
          </CollectionsFormField>
        </div>

        <CollectionsRuleDelete deleteRule={deleteRule} />
      </RuleControls>

      {RuleComponent && (
        <RuleComponent
          retailerId={retailerId}
          control={control}
          register={register}
          formPrefix={formPrefix}
        />
      )}
    </React.Fragment>
  )
}
